
import React, { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthProvider";
import ProductHeader from "@/components/product/ProductHeader";
import ProductImage from "@/components/product/ProductImage";
import ProductActions from "@/components/product/ProductActions";
import ProductReviews from "@/components/product/ProductReviews";
import PersonalNotes from "@/components/product/PersonalNotes";
import SimilarProducts from "@/components/product/SimilarProducts";
import LikeButton from "@/components/product/LikeButton";
import ReviewForm from "@/components/product/ReviewForm";
import NewProductForm from "@/components/product/NewProductForm";
import { Product } from "@/types/product";
import { useToast } from "@/hooks/use-toast";
import { useProductScanner } from "@/hooks/use-product-scanner";

const ProductDetail = () => {
  // Move all hooks to the top level
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [personalNote, setPersonalNote] = useState("");
  const { scanImage } = useProductScanner();
  const [processedText, setProcessedText] = useState<string | undefined>();
  const [isProcessingImage, setIsProcessingImage] = useState(false);
  
  // Always define both queries but conditionally enable them
  const isNewProduct = id === 'new';
  
  // This query is for existing products (enabled only when NOT 'new')
  const {
    data: product,
    isLoading: isProductLoading,
    error: productError
  } = useQuery({
    queryKey: ['product', id],
    queryFn: async () => {
      if (!id) throw new Error('No product ID provided');
      
      console.log("Fetching product with ID:", id);
      const { data, error } = await supabase
        .from('products')
        .select(`
          *,
          reviews (
            rating,
            comment,
            purchase_location,
            user_id,
            profiles (username)
          )
        `)
        .eq('id', id)
        .maybeSingle();

      if (error) {
        console.error("Supabase error:", error);
        throw error;
      }
      
      console.log("Fetched product data:", data);
      return data as Product;
    },
    enabled: !!id && !isNewProduct
  });

  // Define the mutation (used for both new and existing products)
  const saveNoteMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id || !id || isNewProduct) throw new Error('Missing user or product ID');
      
      const { error } = await supabase
        .from('personal_notes')
        .upsert({
          product_id: id,
          user_id: user.id,
          note: personalNote
        });

      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: t('products.noteSaved'),
        description: t('products.personalNoteSaved'),
      });
      queryClient.invalidateQueries({ queryKey: ['personal-note', id] });
    }
  });

  const handleSaveNote = () => {
    if (!user) {
      toast({
        title: t('auth.required'),
        description: t('auth.pleaseLoginFirst'),
      });
      return;
    }
    saveNoteMutation.mutate();
  };

  // Process image for new product if needed
  useEffect(() => {
    const processImage = async () => {
      if (isNewProduct && location.state?.imageFile && !processedText && !isProcessingImage) {
        try {
          setIsProcessingImage(true);
          console.log('Processing image for OCR:', {
            fileName: location.state.imageFile.name,
            fileSize: location.state.imageFile.size,
            type: location.state.imageFile.type
          });
          
          const result = await scanImage(location.state.imageFile);
          if (result?.detectedText) {
            console.log('OCR completed successfully:', {
              textLength: result.detectedText.length,
              hasProducts: result.products?.length > 0,
              productInfo: result.productInfo
            });
            setProcessedText(result.detectedText);
          }
        } catch (error) {
          console.error('Error processing image:', error);
          toast({
            title: t("errors.scanError"),
            description: t("errors.pleaseTryAgain"),
            variant: "destructive",
          });
        } finally {
          setIsProcessingImage(false);
        }
      }
    };

    processImage();
  }, [isNewProduct, location.state, processedText, scanImage, t, toast, isProcessingImage]);

  // Render paths based on if it's a new product or viewing an existing one
  if (isNewProduct) {
    const { detectedText, scanId, imageFile, productInfo } = location.state || {};
    
    // Use processed text if available, otherwise use detectedText from state
    const textToUse = processedText || detectedText;

    console.log("Creating new product with:", {
      detectedText: textToUse,
      scanId,
      hasImage: !!imageFile,
      productInfo
    });
    
    return (
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold mb-4 text-amber-700">{t('products.addNewProduct')}</h1>
        <div className="bg-white rounded-lg shadow p-6">
          <NewProductForm
            detectedText={textToUse}
            scanId={scanId}
            imageFile={imageFile}
            productInfo={productInfo}
          />
        </div>
      </div>
    );
  }

  // Handle loading and error states for existing product details
  if (productError) {
    return <div className="container mx-auto px-4 py-8">Error loading product</div>;
  }

  if (isProductLoading || !id) {
    return <div className="container mx-auto px-4 py-8">Loading...</div>;
  }

  if (!product) {
    return <div className="container mx-auto px-4 py-8">Product not found</div>;
  }

  // Render the existing product details
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <ProductImage imageUrl={product.image_url} productName={product.name} />
        
        <div className="space-y-6">
          <ProductHeader product={product} />
          
          <div className="flex items-center gap-4">
            <ProductActions productId={product.id} price={product.price} />
            <LikeButton productId={product.id} />
          </div>

          <div className="border-t pt-4">
            <h3 className="font-semibold mb-2">{t('products.description')}</h3>
            <p>{product.description}</p>
          </div>

          {product.reviews?.length > 0 && (
            <ProductReviews reviews={product.reviews} />
          )}

          <ReviewForm productId={product.id} />

          {user && (
            <PersonalNotes
              note={personalNote}
              onNoteChange={setPersonalNote}
              onSave={handleSaveNote}
            />
          )}
        </div>
      </div>

      <SimilarProducts 
        currentProductId={product.id} 
        brand={product.brand}
      />
    </div>
  );
};

export default ProductDetail;
